import React, { useState, useEffect, useContext, Fragment } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Skeleton from "@mui/material/Skeleton";
import Paper from "@mui/material/Paper";

import Map from "@/components/Map/Map";

// OpenLayers
import proj4 from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { get as OlGetProjection, Projection } from "ol/proj";
import OlMapBrowserEvent from "ol/MapBrowserEvent";
import OlBaseLayer from "ol/layer/Base";
import OlCollection from "ol/Collection";
import OlSourceVector from "ol/source/Vector";
import Geometry from "ol/geom/Geometry";
import OlFormatWKT from "ol/format/WKT";
import OlFeature from "ol/Feature";
import { DrawEvent } from "ol/interaction/Draw";
import OlDrawInteraction from "ol/interaction/Draw";
import { platformModifierKeyOnly } from "ol/events/condition"
import modelLokacije from "@/models/lokacije";
import { transform } from "ol/proj";
import { Extent as OlExtent, getCenter as OlGetCenter } from "ol/extent";



// Custom
import {
  ButtonControl,
  Controls,
  RotateControl,
  ScaleLineControl,
  ZoomControl,
  ZoomToExtentControl
} from "@/components/Map/Controls";

import { Interactions, DrawInteraction, DefaultInteractions } from "@/components/Map/Interactions";
import {
  measurementsStyle,
  measuringStyle,
} from "@/components/Map/mapStyles";

import { Layers, TileLayer, VectorLayer, GroupLayer } from "@/components/Map/Layers";
import { Overlays, PopupOverlay } from "@/components/Map/Overlays";

import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwticher";
import GeoPortalGSLayer from "@/views/MapPage/GeoPortalGSLayer";
import { padExtent, mergeExtents, containedExtent, findInteraction } from "@/lib/olHelpers";
import modelFishnet from "@/models/fishnets";
import modelPhoto360 from "@/models/photos360";
import dataController from "@/lib/dataController";
import { flattenLayers } from "@/lib/olHelpers";

//Services
import mapService from "@/services/mapService";
import gsService from "@/services/gsService";

import {
  selectedRecordStyle,
  objectAnalysisRecordStyle,
  fishnetStyle,
  imageInfoStyle,
  selectedImageInfoStyle,
  photo360Style,
  lokacijeStyle
} from "@/components/Map/mapStyles";
import { DCRecord, IDataController } from "@/@types/lib/dataController";

//Types
import OperationsContext, { OperationsContextType } from "@/components/OperationsContext/OperationsContext";
import { IMapSettings, IMapView, DrawingType, ViewOptionsType, BoolSettings } from "@/@types/common";
import copyTextToClipboard from "@/lib/copyTextToClipboard";
import {
  CardOverlayType,
  CardPositon,
  ICardOverlayData,
} from "@/@types/views/GIS/map";
import Image360InfoCard from "../../MapPage/Image360InfoCard";
import GSInfoCard, { CardPosition } from "../../MapPage/GSInfoCard";
import { MapBrowserEvent } from "ol";

import { DialogContextType } from "@/@types/ui/DialogContext";
import LocationDialog from "./LocationDialog";
import DialogContext from "@/ui/DialogContext/DialogContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";

import { GFIFeatureType } from "@/services/gsService";

type RadniSastanciMapProps = {
  records: Array<DCRecord>;
  selectedObjekt: DCRecord | null;
  lokacije: Array<DCRecord>;
  onLokacijeSelect: Function;
  selectedImageId: number | null;
  onObjektSelect: Function;
  imagesBasicInfo: Array<DCRecord>;
  mapId: number;
  selectedLokacija: DCRecord | null;
  refreshLocations: () => void;
  selectedObjektFor3D: DCRecord | null;
  currentTab: boolean;
}

function RadniSastanciMap(props: RadniSastanciMapProps) {
//#region Variable Definitions
  const dialogContext = useContext<DialogContextType>(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const operationsContext = useContext(OperationsContext) as OperationsContextType;
  const { t } = useTranslation();

  const [mapInitialized, setMapInitialized] = useState(false);

  const [drawType, setDrawType] = useState<DrawingType | undefined>(undefined);
  const [drawingSource, setDrawingSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [measurementsSource, setMeasurementsSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [measuringFeature, setMeasuringFeature] = useState<OlFeature<Geometry> | undefined>(undefined);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [showLocations, setShowLocations] = useState<boolean>(false);
  const [showObjects, setShowObjects] = useState<boolean>(false);

  const [locationFilter, setLocationFilter] = useState<number[]>([]);

  const [expandedPosjednici, setExpandedPosjednici] = useState(false);
  const [expandedVlasnici, setExpandedVlasnici] = useState(false);

  //@ts-ignore
  const {
    records,
    selectedObjekt,
    onObjektSelect,
    lokacije,
    onLokacijeSelect,
    imagesBasicInfo,
    selectedImageId,
    refreshLocations,
    selectedLokacija,
    selectedObjektFor3D,
    currentTab
  } = props;
  const { mapId } = props;

  const [mapSettings, setMapSettings] = useState<IMapSettings | undefined>();
  const [defaultExtent, setDefaultExtent] = useState<number[] | undefined>();
  const [selectedExtent, setSelectedExtent] = useState(padExtent(defaultExtent));
  const [objExtent, setObjExtent] = useState<number[]>([]);
  // const [imgExtent, setImgExtent] = useState<number[]>([]);
  const [currentExtent, setCurrentExtent] = useState<number[]>([]);
  const [viewOptions, setViewOptions] = useState<IMapView | undefined>();
  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [objektiSource, setObjektiSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [lokacijeSource, setLokacijeSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [selectedSource, setSelectedSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [fishnetsSource, setFishnetsSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [imageInfoSource, setImageInfoSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [selectedImageInfoSource, setSelectedImageInfoSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [photos360Source, setPhotos360Source] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [showDOFLayer, setShowDOFLayer] = useState(true);
  const [showFishnetsLayers, setShowFishnetsLayer] = useState(false);
  const [showZgrade, setShowZgrade] = useState(true);
  const [showImageInfoLayers, setShowImageInfoLayers] = useState(true);
  const [cardOverlay, setCardOverlay] = useState<any | undefined>(undefined);
  const [events, setEvents] = useState<boolean>(false);

  const [locationAddState, setLocationAddState] = useState<boolean>(false);
  const [locationMoveState, setLocationMoveState] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [project3d, setProject3d] = useState(false);

  const [feature, setFeature] = useState<OlFeature<Geometry> | undefined>(
    undefined
  );


  //define proj
  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
  proj4.defs(
    "EPSG:31276",
    "+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);

  const htrs96 = OlGetProjection("EPSG:3765") as Projection;
  const wgs84 = OlGetProjection("EPSG:4326") as Projection;
  const wgs84PM = OlGetProjection("EPSG:3857") as Projection;

  const wkt = new OlFormatWKT();

  const dcFishnets = new dataController(modelFishnet);
  const dcPhotos360 = new dataController(modelPhoto360);
  const dcLokacije: IDataController = new dataController(modelLokacije);

  // Threshold for when objects and locations show up
  const zoomThreshold = 18;
  const EXTENT_PADDING = 20;
  const Z_OFFSET = 90;

//#endregion
//#region handleClick()

const handleObjektClick = (id: string | number | undefined, rec: DCRecord | null) => {
  if (id) {
    onObjektSelect(id, rec);
  } else {
    onObjektSelect(-1, null);
  }
}

const handleDialogClose = (dataChanged: any) => {
  if(dataChanged.dataChanged === true && dataChanged.action === "insert") {
    refreshLocations();
    // window.location.reload();
  } 
}

const generateCardOverlayData = (
  layer: GFIFeatureType[],
  featureType: CardOverlayType,
  pos: CardPositon
) => {
  return {
    position: pos,
    feature: layer[0],
    type: featureType,
    record: undefined,
  } as ICardOverlayData;
};

const handleClick = (evt: OlMapBrowserEvent<any>) => {
  if (drawType) return;
  let hit = false;

  var feature = evt.map.forEachFeatureAtPixel(
    evt.pixel,
    function (feature) {
      return feature;
    },
    {
      layerFilter: (layer) => {
        const layerId = layer.get("id");

        return layerId !== null && layerId !== undefined
          && (layerId.startsWith("objekti")
            || layerId.startsWith("lokacije")
            || layerId.startsWith("photos360"));
      }
    }
  );

  if(locationAddState) {
    // console.log(evt.coordinate);
    const dkpLayerInfo = layersCollection?.getArray().find(x => x.get("id") === "app-dkp");
    if (dkpLayerInfo) {
      const gsLayerName = dkpLayerInfo.get("layer");

      gsService
        .getFeatureInfo(evt.map, evt.pixel, [gsLayerName])
        .then((resp) => {
          if (resp && Object.keys(resp).length != 0) {
            const layerKeys = Object.keys(resp);
            const dkpKey = layerKeys.find((x) => x.substring(4) === "DKP");
            const dkpFeatures = dkpKey ? resp[dkpKey] : [];
            
            if (dkpKey && dkpFeatures.length > 0) {
              const kc = resp[dkpKey][0].properties.kc_broj;
              const ko = resp[dkpKey][0].properties.ko_ime;

              const [cx, cy] = transform(
                [evt.coordinate[0], evt.coordinate[1]],
                "EPSG:3857",
                "EPSG:3765"
              );
          
              const wktString = 'POINT(' + cx + ' ' + cy + ')';
          
              // Open form to insert location
              dialogContext.showDialog(LocationDialog, {
                dc: dcLokacije,
                mode: "insert",
                form: "insert",
                onClose: handleDialogClose,
                coordinates: wktString,
                defaultValues: {
                  kc: kc,
                  ko_ime: ko,
                  lokacija_status_id: 5
                }
              });

              // const showCard = () => showCardOverlay("dkp", data, null);
              // closeOverlays(showCard);
            } 
          } else {
            // closeOverlays(undefined);
          }
        });
    }
    
    refreshLocations();
    setLocationAddState(false);

    return;
  }

  if(locationMoveState === true) {
    const coordinates = evt.coordinate;
    
    const [cx, cy] = transform(
      [coordinates[0], coordinates[1]],
      "EPSG:3857",
      "EPSG:3765"
    );

    // Request
    if(selectedObjekt && selectedObjekt !== null) {
      const newLokacija = {
        id: selectedObjekt.id, 
        kb: selectedObjekt.kb,
        kc: selectedObjekt.kc,
        ul_ime: selectedObjekt.ul_ime,
        na_ime: selectedObjekt.na_ime,
        na_mb: selectedObjekt.na_mb,
        ko_ime: selectedObjekt.ko_ime,
        zona: selectedObjekt.zona,
        ko_mb: selectedObjekt.ko_mb,
        lokacija_status_id: selectedObjekt.lokacija_status_id,
        kn_koef: selectedObjekt.kn_koef,
        ul_jid: selectedObjekt.ul_jid,
        geom: 'POINT(' + cx + ' ' + cy + ')',
        komentar: selectedObjekt.komentar
      }
      const currPath = "operations/projekti/" + operationsContext.selectedProject?.id + "/lokacije"
      dcLokacije.UpdateRecord(Number(selectedObjekt?.id?.toString()), newLokacija, currPath)
        .then((resp: any) => {
          if (resp.success) {
            refreshLocations();
            // window.location.reload();
          }
        }).catch((resp) => {
          //console.log(resp);
        });
    }

    setLocationMoveState(false);

    return;
  }

  if (feature) {
    hit = true;

    const id = feature.getId();
    const rec = feature.getProperties();
    const { type } = (rec || {});
    if (type && type === 'photo360') {
      if (cardOverlay && cardOverlay.data.data.id === id) {
        closeOverlays(null);
      } else {
        const data = {
          position: evt.coordinate as CardPositon,
          data: rec,
          type: "photo360"
        } as ICardOverlayData;

        setCardOverlay({
          type: "photo360",
          data: data
        })
      }
    } else if(rec?.wkt.startsWith('POINT') && id) {
      onLokacijeSelect(id, rec);
    } else {
      handleObjektClick(id, rec);
    }
  } 

  if (!hit) { 
    const dkpLayerInfo = layersCollection?.getArray().find(x => x.get("id") === "app-dkp");
    if (dkpLayerInfo) {
      const gsLayerName = dkpLayerInfo.get("layer");

      gsService
        .getFeatureInfo(evt.map, evt.pixel, [gsLayerName])
        .then((resp) => {
          if (resp && Object.keys(resp).length != 0) {
            const layerKeys = Object.keys(resp);
            const dkpKey = layerKeys.find((x) => x.substring(4) === "DKP");
            const dkpFeatures = dkpKey ? resp[dkpKey] : [];
            
            if (dkpKey && dkpFeatures.length > 0) {
              const data = generateCardOverlayData(
                resp[dkpKey],
                "dkp",
                evt.coordinate as CardPositon
              );
              const showCard = () => showCardOverlay("dkp", data, null);
              closeOverlays(showCard);
            } 
          } else {
            closeOverlays(undefined);
          }
        });
    }
  }
};

//#endregion
//#region useEffect()

  useEffect(() => {
    if(operationsContext.selectedProject?.cesium_folder !== null && operationsContext.selectedProject?.cesium_config_file !== null) {
      setProject3d(true);
    } else {
      setProject3d(false);
    }
  });

  useEffect(() => {
    if (layersCollection && mapSettings && viewOptions) {
      if (events) {
        unregisterEvents();
      }
      registerEvents();
      setEvents(true);
      return () => unregisterEvents()
    }
    return () => {};
  }, [layersCollection, mapSettings, viewOptions, selectedSource])

  useEffect(() => {
    setMapSettings(undefined);
    setViewOptions(undefined);
    setSelectedSource(new OlSourceVector({}));

    if (operationsContext.selectedProject) {
      const projectId = operationsContext.selectedProject.id as number;
      mapService.getDefaultData(mapId, projectId).then((resp) => {
        if (resp) {
          setMapSettings(resp);
        } else {
          setMapSettings(undefined);
        }
      })

      mapService.getLayers(mapId, projectId).then((coll) => {
        setLayersCollection(coll);
        setMapInitialized(true);
      });

      dcFishnets.GetData(`operations/projekti/${operationsContext.selectedProject.id}/fishnets/`)
        .then((resp) => {
          if (resp.success) {
            const src = new OlSourceVector({});
            if (Array.isArray(resp.data) && resp.data.length > 0) {
              resp.data.forEach((x) => {
                let ft = wkt.readFeature(x.wkt, {
                  dataProjection: htrs96,
                  featureProjection: wgs84PM
                })
                if (ft) {
                  ft.setId(x.id as number);
                  ft.setProperties({ id: x.id, sifra: x.sifra });
                  src.addFeature(ft);
                }

              })
            }
            setFishnetsSource(src);
          }
        });

      dcPhotos360.GetData(`operations/projekti/${operationsContext.selectedProject.id}/photos360/`)
        .then((resp) => {
          if (resp.success) {
            const src = new OlSourceVector({});
            if (Array.isArray(resp.data) && resp.data.length > 0) {
              resp.data.forEach((x) => {
                let ft = wkt.readFeature(x.wkt, {
                  dataProjection: wgs84PM,
                  featureProjection: wgs84PM
                })
                if (ft) {
                  ft.setId(x.id as number);
                  ft.setProperties({
                    id: x.id, fileName: x.filename, type: "photo360"
                  });
                  src.addFeature(ft);
                }
              })
            }
            setPhotos360Source(src);
          }
        })

    } else {
      setFishnetsSource(new OlSourceVector({}));
      setPhotos360Source(new OlSourceVector({}));
    }
  }, [operationsContext.selectedProject]);

  useEffect(() => {
    if (mapSettings) {
      setDefaultExtent(mapSettings.default_extent);
      setSelectedExtent(padExtent(mapSettings.default_extent));
      setViewOptions(prevState => {
        return {
          ...prevState,
          center: mapSettings.initial_view_center,
          zoom: mapSettings.initial_view_zoom,
          extent: mapSettings.max_extent,
          minZoom: 8,
          maxZoom: 23
        }
      });
    }

  }, [mapSettings])

  // Objects in fishnet
  useEffect(() => {
    const src = new OlSourceVector({});
    if (Array.isArray(records)) {
      records.forEach((record: DCRecord) => {
        if (record.wkt) {
          let newFeature = new OlFeature({
            geometry: wkt.readGeometry(record.wkt, {
              dataProjection: htrs96,
              featureProjection: wgs84PM
            })
          });
          newFeature.setId(record.id as number);
          newFeature.setProperties({ ...record });
          src.addFeature(newFeature);
        }
      });
    }

    setObjektiSource(src);
  }, [records]);

  // Locations
  useEffect(() => {
    const src = new OlSourceVector({});
    if (Array.isArray(lokacije)) {
      lokacije.forEach((record: DCRecord) => {
        if (record.wkt && !locationFilter.includes(Number(record.lokacija_status_id))) {
          let newFeature = new OlFeature({
            geometry: wkt.readGeometry(record.wkt, {
              dataProjection: htrs96,
              featureProjection: wgs84PM
            })
          });
          newFeature.setId(record.id as number);
          newFeature.setProperties({ ...record });

          src.addFeature(newFeature);
        }
      });
    }

    setLokacijeSource(src);
  }, [lokacije, locationFilter]);

  useEffect(() => {
    const src = new OlSourceVector({});
    let hasWkt = false;
    imagesBasicInfo.forEach((imgInfo: DCRecord) => {
      if (imgInfo.wkt) {
        hasWkt = true;
        let newFeature = new OlFeature({
          geometry: wkt.readGeometry(imgInfo.wkt, {
            dataProjection: htrs96,
            featureProjection: wgs84PM,
          })
        });
        newFeature.setId(imgInfo.imageId as number);
        newFeature.setProperties({ ...imgInfo });
        src.addFeature(newFeature);
      }
    });
    setImageInfoSource(src);
    setSelectedImageInfoSource(new OlSourceVector({}));
    // if (imagesBasicInfo.length && hasWkt) {
    //   setImgExtent(src.getExtent());
    // }
  }, [imagesBasicInfo]);

  useEffect(() => {
    if (showImageInfoLayers) {
      let hasWkt = false;
      const src = new OlSourceVector({});
      const selId = selectedImageId ? selectedImageId : null
      const selected = imagesBasicInfo.find(img => img.imageId === selId);
      if (selId && selected && selected.wkt) {
        hasWkt = true;
        let newFeature = new OlFeature({
          geometry: wkt.readGeometry(selected.wkt, {
            dataProjection: htrs96,
            featureProjection: wgs84PM,
          })
        });
        newFeature.setId(selected.imageId as number);
        newFeature.setProperties({ ...selected });
        src.addFeature(newFeature);
      }
      setSelectedImageInfoSource(src);
      // const imgSelected = selected && selected.wkt;
      // if (src && objExtent.length > 0 && imgSelected && hasWkt) {
      //   const tmpImgExtent = src.getExtent();
      //   if (!containedExtent(tmpImgExtent, currentExtent)) {
      //     setImgExtent(mergeExtents(objExtent, tmpImgExtent, true));
      //   }
      // }
    }
  }, [selectedImageId])

  useEffect(() => {
    if (
      showImageInfoLayers
      && imageInfoSource
      && imageInfoSource.getFeatures().length
      && imagesBasicInfo.length > 0
    ) {
      setObjExtent([]);
      // setImgExtent(imageInfoSource.getExtent());
      if (drawType !== undefined) {
        toggleDrawType();
      }
    }
  }, [showImageInfoLayers]);

  useEffect(() => {
    const src = new OlSourceVector({});
    if (selectedObjekt != null && selectedObjekt.wkt !== undefined && selectedObjekt.wkt !== null) {
      let ft = wkt.readFeature(selectedObjekt.wkt, {
        dataProjection: htrs96,
        featureProjection: wgs84PM
      })
      if (ft) {
        ft.setId(selectedObjekt.id as number);
        src.addFeature(ft);
      }
      setShowImageInfoLayers(true);
    }
    setSelectedSource(src);
    if (src && selectedObjekt && selectedObjekt.wkt) {
      setObjExtent([]);
      const extent = padExtent(src.getExtent(), EXTENT_PADDING);
      if (extent[0] === extent[2] && extent[1] === extent[3]) {
        // Adjust zoom for locations - possibly dumb
        extent[0] = extent[0] - 20;
        extent[2] = extent[2] + 20;
        extent[1] = extent[1] - 20;
        extent[3] = extent[3] + 20;
      }
      // console.log(extent);
      setSelectedExtent(extent);
    } else {
      /*
      if (defaultExtent) {
        setSelectedExtent(defaultExtent);
      }
      */
    }
  }, [selectedObjekt]);

//#endregion
//#region Helpers

  const eventRegistrationFn = (evt: KeyboardEvent) => {
    if (evt.altKey) {
      switch (evt.code) {
        case 'KeyZ':
        case 'KeyY': setShowImageInfoLayers(prev => !prev); break;
        case 'KeyX': toggleDrawType(); break;
        case 'KeyC': setObjExtent(padExtent(selectedSource.getExtent(), EXTENT_PADDING)); break;
        case 'KeyO': setShowObjects(showObjects => !showObjects); break;
        case 'KeyL': setShowLocations(showLocations => !showLocations); break;
        case 'KeyM': 
          if(selectedLokacija) {
            setLocationMoveState(locationMoveState => !locationMoveState) 
          } else if(snackbarContext) {
            snackbarContext.showNotification(t("users.no_move_location"), "warning");
          }
          break;
        case 'KeyN': setLocationAddState(locationAddState => !locationAddState); break;
        case 'KeyD': {
          if (project3d) {
            evt.preventDefault();
            toggleView3d(); 
          }
          break;
        }
        // case 'Digit1': handleFilter(1); break;
        // case 'Digit2': handleFilter(2); break;
        // case 'Digit3': handleFilter(3); break;
        // case 'Digit4': handleFilter(4); break;
        // case 'Digit5': handleFilter(5); break;
        // case 'Digit6': handleFilter(6); break;
      }
    }
  }

  const registerEvents = () => {
    window.addEventListener('keydown', eventRegistrationFn);
  }

  const unregisterEvents = () => {
    window.removeEventListener('keydown', eventRegistrationFn)
  };

  const handleLayerSwitcherDOFClick = () => {
    setShowDOFLayer((prevState) => {
      return !prevState
    });
  };

  const handleLayerSwitcherFishnetClick = () => {
    setShowFishnetsLayer((prevState) => {
      return !prevState
    });
  };

  const toggleZgrade = () => {
    setShowZgrade((prevState) => {
      return !prevState
    });
  }

  const handleDrawMeasureStart = (evt: DrawEvent) => {
    setMeasurementsSource(
      (prevState: OlSourceVector<Geometry>) =>
        new OlSourceVector({
          features: []
        })
    );
    if (drawingSource.getFeatures().length > 0) {
      setDrawingSource(new OlSourceVector({}));
    }

    setMeasuringFeature(evt.feature);
  };

  const handleDrawMeasureEnd = (evt: DrawEvent) => {
    setDrawingSource(new OlSourceVector({}));
    setMeasurementsSource(
      (prevState: OlSourceVector<Geometry>) =>
        new OlSourceVector({
          features: [evt.feature]
        })
    );

    setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
  };

  const drawCondition = (evt: MapBrowserEvent<any>) => {
    var isCtrl = platformModifierKeyOnly(evt);
    if (isCtrl) {
      const int = findInteraction(evt.map, OlDrawInteraction);
      if (int) {
        // console.log('removing last point...');
        (int as OlDrawInteraction).removeLastPoint();
      }
    }
    return isCtrl ? false : true;
  }

  const handleEraseMeasurements = () => {
    setMeasurementsSource(new OlSourceVector({}));
  };

  const toggleDrawType = () => {
    setDrawType((prevState: DrawingType | undefined) => {
      const newState = prevState === undefined ? 'Polygon' : prevState === 'Polygon' ? 'LineString' : undefined;
      if (!newState) {
        handleEraseMeasurements();
        setDrawingSource(new OlSourceVector({}));
        setShowImageInfoLayers(true);
      } else {
        setShowImageInfoLayers(false);
        if (selectedSource.getFeatures().length > 0) {
          const extent = padExtent(selectedSource.getExtent())
          // setObjExtent(extent);
        }
      }
      return newState;
    });
  }

  const copyValue = (n: number) => {
    copyTextToClipboard(`${n.toFixed(2)}`);
  }

  const showCardOverlay = (
    type: CardOverlayType,
    data: ICardOverlayData,
    ft: OlFeature<any> | null
  ) => {
    //TODO: refactor
    feature ? feature.setProperties({ selected: false }) : null;

    if (ft) {
      ft.setProperties({ selected: true });
    }

   
    setCardOverlay({
      type: type,
      data: data,
    });
    if (ft) {
      setFeature(ft);
    } else {
      setFeature(undefined);
    }
    
  };

  const closeOverlays = (callback: any) => {
    setCardOverlay(undefined);
    setExpandedPosjednici(false);
    setExpandedVlasnici(false);
    if (callback !== undefined && typeof callback === "function") {
      callback();
    }
  };

  const handleZoomChange = (zoom: number) => {
    if(zoomLevel === 0) {
      setZoomLevel(zoom);
      return;
    }
    if(zoom > zoomThreshold && zoomLevel < zoomThreshold) {
      setShowObjects(true);
      setShowLocations(true);
    } else if(zoom < zoomThreshold && zoomLevel > zoomThreshold) {
      setShowObjects(false);
      setShowLocations(false);
    }

    setZoomLevel(zoom);
  }

  const handleFilter = (status: number) => {
    const newLocationFilter = [...locationFilter];
    if(locationFilter.includes(status)) {
      delete newLocationFilter[newLocationFilter.indexOf(status)];
      setLocationFilter(() => { return newLocationFilter; });
    } else {
      newLocationFilter.push(status);
      setLocationFilter(() => { return newLocationFilter; });
    }
  }

  const toggleView3d = () => {
    if (selectedObjektFor3D && selectedObjektFor3D !== null && currentTab === true) {
      const ft = selectedSource.getFeatures().find((x) => x.getId() === selectedObjektFor3D?.id);
      const extent = ft?.getGeometry()?.getExtent() as OlExtent;

      // console.log(JSON.stringify(ft?.getGeometry()));
      // console.log('FT: ' + JSON.stringify(ft));
      // console.log('Extent: ' + extent);

      /* Potencijalno korisno nakon sto se dodaju podaci o visini u bazu
      const [cx, cy] = transform(
        [ft?.getGeometry(), ft?.getProperties().cy],
        "EPSG:3765",
        "EPSG:4326"
      ).map((x) => (x * Math.PI) / 180);
      */
      
      // const [cx1, cy1] = transform(
      //   [(extent[0] + extent[2])/2, (extent[1] + extent[3])/2],
      //   "EPSG:3857",
      //   "EPSG:4326"
      // ).map((x) => (x * Math.PI) / 180);
      // // console.log(cx1, cy1);
      
      let cx,cy,cz;
      
      if (selectedObjektFor3D.cx === null || selectedObjektFor3D.cy === null) // NEMA CENTROIDA :(
      {
        [cx,cy] = transform(
          [(extent[0] + extent[2])/2, (extent[1] + extent[3])/2],
          "EPSG:3857",
          "EPSG:4326"
        ).map((x) => (x * Math.PI) / 180);
        cz=150; // statična visina jer nemamo podatak o visini
      }
      else // IMA CENTROIDA :)
      {
        [cx, cy] = transform(
          [selectedObjektFor3D?.cx as number, selectedObjektFor3D?.cy as number],
          "EPSG:3765",
          "EPSG:4326"
        ).map((x) => (x * Math.PI) / 180);
        cz = selectedObjektFor3D?.cz ? (selectedObjektFor3D.cz as number) + Z_OFFSET : Z_OFFSET;
      }

      // navigate("/3d", { state: { cx: cx, cy: cy, cz: record?.cz}}) //(opens 3d in the same tab)
      // const [cx, cy, cz] = [0,0,0];
      const url =
        location.origin +
        "/3d-view?cx=" +
        cx?.toString() +
        "&cy=" +
        cy?.toString() +
        "&cz=" +
        cz?.toString() + 
        "&pid=" +
        operationsContext.selectedProject?.id?.toString();
      // console.log('url: ' + url);

      window.open(url, "_blank"); // opens 3d in a new tab
    } else {
      if (snackbarContext) {
        snackbarContext.showNotification(t("users.no_3d_object"), "warning");
      }
      // alert('Odaberite objekt za 3d prikaz');
    }
  }

  const toggleGoToStreetView = () => {
    if (selectedObjektFor3D && selectedObjektFor3D !== null && currentTab === true) {
      const ft = selectedSource.getFeatures().find((x) => x.getId() === selectedObjektFor3D?.id);
      let cx, cy;

      if (selectedObjektFor3D.cx === null || selectedObjektFor3D.cy === null) {
        const extent = ft?.getGeometry()?.getExtent() as OlExtent | undefined;
        if (!extent) {
          if (snackbarContext) {
            snackbarContext.showNotification(t("users.no_object"), "warning");
          }
          return;
        }
        [cy, cx] = transform(
          [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
          "EPSG:3857",
          "EPSG:4326"
        );
      } else {
        [cy, cx] = transform(
          [selectedObjektFor3D.cx as number, selectedObjektFor3D.cy as number],
          "EPSG:3765",
          "EPSG:4326"
        );
      }

      const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${cx.toString()},${cy.toString()}`;

      window.open(url, "_blank");
    } else {
      if (snackbarContext) {
        snackbarContext.showNotification(t("users.no_object"), "warning");
      }
    }
  }

  const toggleGoToGeoportal = () => {
    if (!geoportalUrl) {
      return;
    }

    const center = currentExtent.length === 4
      ? OlGetCenter(currentExtent)
      : viewOptions?.center;
    const zoom = zoomLevel || viewOptions?.zoom;

    if (!center || typeof zoom !== "number") {
      return;
    }

    const normalizedGeoportalUrl = /^(https?:)?\/\//i.test(geoportalUrl)
      ? geoportalUrl
      : `https://${geoportalUrl}`;
    const baseUrl = normalizedGeoportalUrl.replace(/\/+$/, "");
    const url = `${baseUrl}/map?x=${center[0]}&y=${center[1]}&zoom=${Math.round(zoom)}#comments`;

    window.open(url, "_blank");
  }

  const image360Overlay = cardOverlay && cardOverlay.type === "photo360" ? cardOverlay : undefined;

  // const gsOverlay = cardOverlay && cardOverlay.type === "gsinfo" ? cardOverlay : undefined;
  const gsOverlay = cardOverlay  && cardOverlay.type === "dkp" ? cardOverlay : undefined;

  const geoportalUrl = typeof operationsContext.selectedProject?.geoportal_url === "string"
    ? operationsContext.selectedProject.geoportal_url.trim()
    : "";
  const hasGeoportalUrl = geoportalUrl.length > 0;

//#endregion 
//#region Return

  return (
    <Paper elevation={0} sx={{ p: 1, width: "100%", height: "100%", /*minHeight: 500*/ }}>
      {layersCollection && mapSettings && viewOptions ? (
        <Map
          height={"2"}
          view={viewOptions}
          onClick={handleClick}
          //onPointermove={handlePointerMove}
          className="map"
          id="mini-map"
          zoomToExtent={
            objExtent.length
              ? objExtent
                : selectedExtent
          }
          onMoveend={({ map }: OlMapBrowserEvent<any>) => {
            const view = map ? map.getView() : null;
            const extent = view ? view.calculateExtent() : null;
            if (extent && extent.length === 4) {
              setCurrentExtent([...extent]);
            }
          }}
          // zoomToExtentPadding={[50, 50, 50, 50]}
          disableExtentPadding={true}
          initialized={mapInitialized}
          zoomTrigger={handleZoomChange}
        >
          <Controls>
            <ZoomControl zoomInTipLabel={t("map:controls.zoom_in")} zoomOutTipLabel={t("map:controls.zoom_out")} />
            <RotateControl tipLabel={`${t("map:info.reset-rotation")}. ${t("map:info.rotate-the-map")}`} autoHide={true} />
            <ScaleLineControl />
            <GeoBaseLayerSwitcher allowNoLayer display={!showDOFLayer} />
            <ZoomToExtentControl
              id="zoom-extent-default"
              extent={defaultExtent}
              tipLabel={t("map:controls.zoom_to_extent")}
              className="ol-control ol-zoom-extent"
            />
            <ZoomToExtentControl
              id="zoom-extent-selected"
              extent={selectedExtent}
              tipLabel={t("map:controls.zoom_to_selected_shortcut")}
              className="ol-control ol-zoom-selected"
              labelClass="fas fa-bullseye"
            />
            <ButtonControl
              id="layerswitcher-dof"
              className={`ol-control ol-layerswitcher-dof ${showDOFLayer ? "ol-active" : ""}`}
              html="<i class='fas fa-camera'></i>"
              handleClick={handleLayerSwitcherDOFClick}
              title={t("map:layerswitcher.toggle_dof")}
            />
            <ButtonControl
              id="layerswitcher-fishnet"
              className={`ol-control ol-layerswitcher-fishnet ${showFishnetsLayers ? "ol-active" : ""}`}
              html="<i class='fas fa-border-all'></i>"
              handleClick={handleLayerSwitcherFishnetClick}
              title={t("map:layerswitcher.toggle_fishnet")}
            />
            <ButtonControl
              id="add-location"
              className={`ol-control ol-add-location ${locationAddState ? "ol-active" : ""}`}
              html="<i class='fas fa-plus'></i>"
              handleClick={() => {setLocationAddState(!locationAddState)}}
              title={t("map:layerswitcher.add-location")}
            />
            <ButtonControl
              id="layerswitcher-measure"
              className={`ol-control ol-measure-button-alt ${drawType !== undefined ? "ol-active" : ""}`}
              html={drawType === "LineString" ? 
                "<i class='fas fa-ruler'></i>" : 
                drawType === "Polygon" ? 
                  "<i class='fas fa-draw-polygon'></i>" : 
                  "<i class='fas fa-pencil-ruler'></i>"
                }
              handleClick={toggleDrawType}
              title={drawType === "LineString" ? 
                t("map:layerswitcher.active_measure_linestring") : 
                drawType === "Polygon" ? 
                  t("map:layerswitcher.active_measure_polygon") : 
                  t("map:layerswitcher.toggle_measure")
              }
            />
            <ButtonControl
              id="layerswitcher-move-location"
              className={`ol-control ol-move-location ${locationMoveState === true ? "ol-active" : ""} ${selectedLokacija ? "" : "ol-inactive-move"}`}
              html="<i class='fas fa fa-arrow-right'></i>"
              handleClick={() => {selectedLokacija ? setLocationMoveState(!locationMoveState) 
                : (snackbarContext ? snackbarContext.showNotification(t("users.no_move_location"), "warning") : null)}}
              title={t("map:layerswitcher.move-location")}
            />
            {layersCollection.getArray().find(x => (x.get("id") === "app-zgrade" || x.get("id") === "app-zgr_op")) ?
              <ButtonControl
                id="toggle-zgrade"
                className={`ol-control ol-layerswitcher-zgrade-alt ${showZgrade ? "ol-active" : ""}`}
                html="<i class='fas fa-building'></i>"
                handleClick={toggleZgrade}
                title={t("map:layerswitcher.toggle-zgrade")}
              /> : null}
            <ButtonControl
              id="layerswitcher-show-locations"
              className={`ol-control ol-show-locations ${showLocations === true ? "ol-active" : ""}`}
              html="<i class='fas fa fa-map-marker'></i>"
              handleClick={() => {setShowLocations(!showLocations)}}
              title={t("map:layerswitcher.show-locations")}
            />
            <ButtonControl
              id="layerswitcher-show-objects"
              className={`ol-control ol-show-objects ${showObjects === true ? "ol-active" : ""}`}
              html="<i class='fas fa-object-group'></i>"
              handleClick={() => {setShowObjects(!showObjects)}}
              title={t("map:layerswitcher.show-objects")}
            />
            <ButtonControl
              id="layerswitcher-filter-locations-1"
              className={`ol-control ol-filter-locations-1 ${!locationFilter.includes(1) ? "ol-active" : ""}`}
              html="<a>1</a>"
              handleClick={() => {handleFilter(1)}}
              title={t("map:layerswitcher.filter-locations") + ' (OK)'}
            />
            <ButtonControl
              id="layerswitcher-filter-locations-2"
              className={`ol-control ol-filter-locations-2 ${!locationFilter.includes(2) ? "ol-active" : ""}`}
              html="<a>2</a>"
              handleClick={() => {handleFilter(2)}}
              title={t("map:layerswitcher.filter-locations") + ' (Brisati)'}
            />
            <ButtonControl
              id="layerswitcher-filter-locations-3"
              className={`ol-control ol-filter-locations-3 ${!locationFilter.includes(3) ? "ol-active" : ""}`}
              html="<a>3</a>"
              handleClick={() => {handleFilter(3)}}
              title={t("map:layerswitcher.filter-locations") + ' (Pomaknuto)'}
            />
            <ButtonControl
              id="layerswitcher-filter-locations-4"
              className={`ol-control ol-filter-locations-4 ${!locationFilter.includes(4) ? "ol-active" : ""}`}
              html="<a>4</a>"
              handleClick={() => {handleFilter(4)}}
              title={t("map:layerswitcher.filter-locations") + ' (Pomaknuto XY)'}
            />
            <ButtonControl
              id="layerswitcher-filter-locations-5"
              className={`ol-control ol-filter-locations-5 ${!locationFilter.includes(5) ? "ol-active" : ""}`}
              html="<a>5</a>"
              handleClick={() => {handleFilter(5)}}
              title={t("map:layerswitcher.filter-locations") + ' (Novo)'}
            />
            <ButtonControl
              id="layerswitcher-filter-locations-6"
              className={`ol-control ol-filter-locations-6 ${!locationFilter.includes(6) ? "ol-active" : ""}`}
              html="<a>6</a>"
              handleClick={() => {handleFilter(6)}}
              title={t("map:layerswitcher.filter-locations") + ' (Dodano)'}
            />
            <ButtonControl
              id="view-3d"
              className={`ol-control ol-3d-view-alt ${selectedObjektFor3D && currentTab ? "" : "ol-inactive-3d"} ${project3d ? "" : "ol-nonexistant-3d"}`}
              html={"<i class='fas fa-globe-americas'></i>"}
              handleClick={toggleView3d}
              title={t("map:layerswitcher.toggle_3d")}
            />
            <ButtonControl
              id="view-street-view"
              className={`ol-control ol-street-view-alt ${selectedObjektFor3D && currentTab ? "" : "ol-inactive-street"}`}
              html={"<i class='fas fa-street-view'></i>"}
              handleClick={toggleGoToStreetView}
              title={t("map:layerswitcher.toggle_street_view")}
            />
            {/*hasGeoportalUrl ?
              <ButtonControl
                id="view-geoportal-map"
                className="ol-control ol-geoportal-view-alt"
                html={"<i class='fas fa-map-marked-alt'></i>"}
                handleClick={toggleGoToGeoportal}
                title={t("map:layerswitcher.toggle_geoportal")}
              />
          : null*/}
            <ButtonControl
                id="view-geoportal-map"
                className="ol-control ol-geoportal-view-alt"
                html={"<i class='fas fa-map-marked-alt'></i>"}
                handleClick={toggleGoToGeoportal}
                title={t("map:layerswitcher.toggle_geoportal")}
              />

          </Controls>
          <Layers>
            {layersCollection ? (
              <>
                {layersCollection.getArray().map((layer: OlBaseLayer, i: number) => {

                  const id = layer.get("id");
                  const visible = id === "app-dof" ? showDOFLayer 
                    : (id === "app-zgrade" || id === "app-zgr_op") ? showZgrade 
                    : true;

                  return (
                    <GeoPortalGSLayer
                      key={i}
                      id={id}
                      title={t(layer.get("title"))}
                      layer={layer}
                      query={layer.get("query")}
                      zIndex={layer.get("zIndex")}
                      visible={visible}
                    />
                  );
                })}
              </>
            ) : null}
            <VectorLayer id="fishnets" source={fishnetsSource} style={fishnetStyle} zIndex={800} visible={showFishnetsLayers} />
            <VectorLayer id="lokacije" source={lokacijeSource} style={showLocations === true ? lokacijeStyle : null} zIndex={902} />
            <VectorLayer id="objekti" source={objektiSource} style={showObjects === true ? objectAnalysisRecordStyle : null} zIndex={900} />
            <VectorLayer id="photos360" source={photos360Source} style={photo360Style} zIndex={905} visible={true} />
            <VectorLayer id="image-info" source={imageInfoSource} style={imageInfoStyle} zIndex={910} visible={showImageInfoLayers} />
            <VectorLayer id="selected" source={selectedSource} style={selectedRecordStyle} zIndex={950} />
            <VectorLayer id="image-info-selected" source={selectedImageInfoSource} style={selectedImageInfoStyle} zIndex={960} visible={showImageInfoLayers} />
            <VectorLayer
              id="measurements"
              source={measurementsSource}
              //@ts-ignore
              style={(f: any, ...rest) => (f.__callback = copyValue, measurementsStyle(f, ...rest))}
              zIndex={900}
            // title="Temp measurements"
            />
            {/* <VectorLayer id="highlighted" source={highlightedSource} style={hoveringRecordStyle} zIndex={960} /> */}
          </Layers>
          <Overlays>
            <PopupOverlay
              id="feature-overlay"
              position={image360Overlay ? image360Overlay.data.position : gsOverlay ? gsOverlay.data.position : undefined}
              autoPan={false}
              onClose={() => { }}
            >
              { cardOverlay && gsOverlay ? (
                  <GSInfoCard
                    featureData={gsOverlay.data}
                    onClose={closeOverlays}
                    expandedVlasnici={expandedVlasnici}
                    setExpandedVlasnici={setExpandedVlasnici}
                    expandedPosjednici={expandedPosjednici}
                    setExpandedPosjednici={setExpandedPosjednici}
                  />
                ) : null }
              {image360Overlay ? <Image360InfoCard id={image360Overlay.data.data.id} fileName={image360Overlay.data.data.fileName} onClose={closeOverlays} /> : null}
            </PopupOverlay>
          </Overlays>
          <Interactions>
            <DefaultInteractions />
            {drawType? (
              <DrawInteraction
                source={drawingSource}
                type={drawType}
                style={measuringStyle}
                onDrawstart={handleDrawMeasureStart}
                onDrawend={handleDrawMeasureEnd}
                condition={drawCondition}
              />
            ) : null}
          </Interactions>
        </Map>
      )
        : <Skeleton animation={false} sx={{ width: "100%", height: "100%", /*minHeight: 500*/ }} />
      }
    </Paper>
  );
}

export default RadniSastanciMap;
